import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useDrag } from 'react-use-gesture';
import { animated, useSpring, config } from 'react-spring';
import { ArrowLeftShort } from '@styled-icons/bootstrap/ArrowLeftShort';
import { convertHexToRGBA } from 'utils/color';
import { useTheme } from 'styled-components';
import clamp from 'lodash/clamp';
const navItemWidth = 34;
const visibleSteps = 3;
const backButtonWidth = 36;
const Container = styled.div `
  position: absolute;
  width: ${({ width }) => width}px;
  height: 55px;
  touch-action: none;
  z-index: 100;
  display: flex;
  align-items: center;
  background: transparent;

  .track {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    top: 0;
  }
`;
const BackButton = styled.a `
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  z-index: 2;

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => convertHexToRGBA(theme.palette.primary.main, 0.2)};
  }
`;
const NavItem = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  left: ${({ index, containerWidth }) => index * (containerWidth / 2 - navItemWidth / 2)}px;
  width: ${navItemWidth}px;
  height: ${navItemWidth}px;
  transform: translateX(0);
  transition: transform 0.25s ease-in-out;

  ${({ backButton, active, index, containerWidth }) => backButton &&
    !active &&
    `
      left: ${index * (containerWidth / 2 - navItemWidth / 2) - backButtonWidth}px;
    `}

  ${({ nextButOne, index, containerWidth, backButton }) => nextButOne &&
    `
    left: calc(${backButton
        ? index * (containerWidth / 2 - navItemWidth / 2) - backButtonWidth
        : index * (containerWidth / 2 - navItemWidth / 2)}px - 8px);
    right: 8px;
    
    &:after {
      content: none;
    }
  `}

  ${({ next, containerWidth }) => next &&
    `
    transform: translateX(${containerWidth / 2 - 72}px);

    &:after {
      content: none;
    }
  `}

  ${({ active, title, containerWidth, backButton }) => active &&
    `
    &:after {
      width: calc((${backButton
        ? containerWidth / 2 -
            navItemWidth * 2 +
            containerWidth / 3 -
            4 -
            backButtonWidth
        : containerWidth / 2 - navItemWidth * 2 + containerWidth / 3 - 4}px - 30px) / 3);
      transform: translateX(calc(100% + 4px)) scale(3.0, 0.5);
    }

    &:before {
      content: "${title}";
      position: absolute;
      font-size: 16px;
      right: 0;
      transform: translateX(calc(100% + 4px));
      z-index: 1;
      font-weight: 700;
      line-height: 1;
      padding: 4px 24px 4px 4px;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: ${backButton
        ? containerWidth / 2 -
            navItemWidth * 2 +
            containerWidth / 3 -
            4 -
            backButtonWidth
        : containerWidth / 2 - navItemWidth * 2 + containerWidth / 3 - 4}px;
    }
  `}
`;
const ClickPadding = styled.div `
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;
const Index = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 16px;
  font-weight: 700;
  transition: all 0.125s ease-out;
  box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.6);

  ${({ indicate }) => indicate &&
    `
    animation: indicate 1.6s ease-in-out infinite;
    cursor: pointer;
  `}

  ${({ active, theme }) => active &&
    `
    color: #fff;
    background: ${theme.palette.grey[800]};
    font-weight: 700;
    border-color: ${theme.palette.grey[800]};
  `}


  @keyframes indicate {
    0% {
      transform: scale(1);
    }

    50% {
      transform: scale(1.2);
    }

    100% {
      transform: scale(1);
    }
  }
`;
const StepNavigation = ({ count, onChange, width, className = '', activeIndex, activeStepTitle, backButton, }) => {
    const { palette } = useTheme();
    const steps = useMemo(() => Array.from(Array(count).keys()), [count]);
    const [clonedSteps, setClonedSteps] = useState([...steps]);
    const [internalIndex, setInternalIndex] = useState(activeIndex);
    const isFirst = internalIndex === 0;
    const isLast = internalIndex === clonedSteps.length - 1;
    const [{ x }, set] = useSpring(() => ({
        x: 0,
        config: config.stiff,
    }));
    const [didInteract, setDidInteract] = useState(false);
    const handleNext = (next) => {
        const nextIndex = next ? internalIndex + 1 : internalIndex - 1;
        setInternalIndex(nextIndex);
    };
    const handleClickBack = () => {
        setInternalIndex(clamp(internalIndex - 1, 0, clonedSteps.length - 1));
    };
    const setX = (index) => {
        set(() => ({
            x: -(index * (width / 2 - navItemWidth / 2)),
        }));
    };
    useEffect(() => {
        setX(internalIndex);
        // if at end of steps add another array
        if (internalIndex + visibleSteps >= clonedSteps.length) {
            setClonedSteps([...clonedSteps, ...steps]);
        }
        if (internalIndex % count === 0) {
            setClonedSteps([...steps]);
            setInternalIndex(0);
            onChange(clonedSteps[internalIndex]);
        }
        else {
            onChange(internalIndex % count);
        }
    }, [internalIndex]);
    useEffect(() => {
        setInternalIndex(activeIndex);
    }, [activeIndex]);
    useEffect(() => setClonedSteps([...steps]), [steps]);
    const bind = useDrag(({ last, movement: [mx], direction: [dx], offset: [offsetX], intentional, }) => {
        if ((isFirst && dx > 0) || (isLast && dx < 0))
            return;
        const threshold = intentional && Math.abs(offsetX) > width / 4;
        if (last && threshold) {
            handleNext(dx < 0);
        }
        else if (last && !threshold) {
            setX(activeIndex);
        }
        else {
            set(() => ({
                x: mx,
            }));
        }
    }, { initial: () => [x.get(), 0], lockDirection: true });
    return (React.createElement(Container, Object.assign({ id: "step-navigation-container", "data-tracking-total-steps": steps.length, className: className, width: width }, bind()),
        backButton && activeIndex !== 0 && (React.createElement(BackButton, { onClick: handleClickBack },
            React.createElement(ArrowLeftShort, { size: 24, color: palette.grey['A800'] }))),
        React.createElement(animated.div, { className: "track", style: {
                transform: x.to((x) => `translateX(${x}px)`),
            } }, clonedSteps.map((stepIndex, index) => {
            const active = index === internalIndex;
            return (React.createElement(NavItem
            // eslint-disable-next-line react/no-array-index-key
            , { 
                // eslint-disable-next-line react/no-array-index-key
                key: `nav-item-${index}`, containerWidth: width, index: index, active: active, past: stepIndex < activeIndex, last: (stepIndex + 1) % steps.length === 0, next: clonedSteps[internalIndex + 1] === stepIndex, nextButOne: clonedSteps[internalIndex + 2] === stepIndex && !active, title: activeStepTitle, onClick: () => {
                    setInternalIndex(index);
                    setDidInteract(true);
                }, backButton: backButton && activeIndex !== 0, "data-cy": "product-journey-step-navigation" },
                React.createElement(ClickPadding, { onClick: () => setInternalIndex(index), "data-tracking-step-index": stepIndex }),
                React.createElement(Index, { active: active, indicate: index === 1 && !didInteract && activeIndex !== 1 }, stepIndex + 1)));
        }))));
};
export default StepNavigation;
